import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  TextField,
  FormControlLabel,
  Checkbox,
  Typography,
  Box,
  MenuItem,
} from '@mui/material';
import { AttachMoney } from '@mui/icons-material';
import { Controller, useForm } from 'react-hook-form';
import { Unit, UnitType } from '../services/api';

interface UnitFormData {
  unit_number: string;
  type: string;
  name: string;
  rent_amount: number;
  bedrooms: number;
  bathrooms: number;
  square_feet: number;
  status: 'vacant' | 'occupied' | 'maintenance';
  description?: string;
  rental_types: ('Hour' | '3 Hours' | 'Daily' | 'Monthly' | 'Yearly')[];
  living_room_count?: number;
  mens_guest_room_count?: number;
  womens_guest_room_count?: number;
  single_bed_count?: number;
  double_bed_count?: number;
  max_persons?: number;
  checkin_time?: string;
  checkout_time?: string;
  tax_rate?: number;
}

interface EditUnitDialogProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (data: UnitFormData) => void;
  unit?: Unit;
  unitTypes: UnitType[];
  isLoading?: boolean;
  updateAllRates?: boolean;
  onUpdateAllRatesChange?: (checked: boolean) => void;
}

const EditUnitDialog: React.FC<EditUnitDialogProps> = ({
  open,
  onClose,
  onSubmit,
  unit,
  unitTypes,
  isLoading = false,
  updateAllRates = false,
  onUpdateAllRatesChange,
}) => {
  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<UnitFormData>({
    defaultValues: {
      unit_number: unit?.unit_number || '',
      type: unit?.type || '',
      name: unit?.name || '',
      rent_amount: unit?.rent_amount || 0,
      bedrooms: unit?.bedrooms || 0,
      bathrooms: unit?.bathrooms || 0,
      square_feet: unit?.square_feet || 0,
      status: unit?.status || 'vacant',
      description: unit?.description || '',
      rental_types: unit?.rental_types || [],
      living_room_count: unit?.living_room_count || 0,
      mens_guest_room_count: unit?.mens_guest_room_count || 0,
      womens_guest_room_count: unit?.womens_guest_room_count || 0,
      single_bed_count: unit?.single_bed_count || 0,
      double_bed_count: unit?.double_bed_count || 0,
      max_persons: unit?.max_persons || 0,
      checkin_time: unit?.checkin_time || '',
      checkout_time: unit?.checkout_time || '',
      tax_rate: unit?.tax_rate || 0,
    },
  });

  // Reset form when unit changes
  React.useEffect(() => {
    if (unit) {
      reset({
        unit_number: unit.unit_number || '',
        type: unit.type || '',
        name: unit.name || '',
        rent_amount: unit.rent_amount || 0,
        bedrooms: unit.bedrooms || 0,
        bathrooms: unit.bathrooms || 0,
        square_feet: unit.square_feet || 0,
        status: unit.status || 'vacant',
        description: unit.description || '',
        rental_types: unit.rental_types || [],
        living_room_count: unit.living_room_count || 0,
        mens_guest_room_count: unit.mens_guest_room_count || 0,
        womens_guest_room_count: unit.womens_guest_room_count || 0,
        single_bed_count: unit.single_bed_count || 0,
        double_bed_count: unit.double_bed_count || 0,
        max_persons: unit.max_persons || 0,
        checkin_time: unit.checkin_time || '',
        checkout_time: unit.checkout_time || '',
        tax_rate: unit.tax_rate || 0,
      });
    }
  }, [unit, reset]);

  const handleFormSubmit = (data: UnitFormData) => {
    onSubmit(data);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <form onSubmit={handleSubmit(handleFormSubmit)}>
        <DialogTitle>Edit Unit</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={6}>
              <Controller
                name="unit_number"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    label="Unit Number"
                    error={!!errors.unit_number}
                    helperText={errors.unit_number?.message}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Controller
                name="type"
                control={control}
                rules={{ required: 'Type is required' }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    select
                    label="Type"
                    error={!!errors.type}
                    helperText={errors.type?.message}
                  >
                    {unitTypes.map((type: UnitType) => (
                      <MenuItem key={type.id} value={type.name}>
                        {type.label}
                      </MenuItem>
                    ))}
                  </TextField>
                )}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Controller
                name="name"
                control={control}
                rules={{ required: 'Name is required' }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    label="Unit Name"
                    error={!!errors.name}
                    helperText={errors.name?.message}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12}>
              <Typography variant="subtitle2" gutterBottom>
                Rental Types *
              </Typography>
              <Controller
                name="rental_types"
                control={control}
                rules={{ required: 'At least one rental type is required' }}
                render={({ field }) => (
                  <Box>
                    <Grid container spacing={2}>
                      {(['Hour', '3 Hours', 'Daily', 'Monthly', 'Yearly'] as const).map((type) => (
                        <Grid item xs={12} sm={6} md={4} key={type}>
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={(field.value || []).includes(type)}
                                onChange={(e) => {
                                  const currentValue = field.value || [];
                                  if (e.target.checked) {
                                    field.onChange([...currentValue, type]);
                                  } else {
                                    field.onChange(currentValue.filter((t: string) => t !== type));
                                  }
                                }}
                              />
                            }
                            label={type}
                          />
                        </Grid>
                      ))}
                    </Grid>
                    {errors.rental_types && (
                      <Typography variant="caption" color="error" sx={{ mt: 1, display: 'block' }}>
                        {errors.rental_types.message}
                      </Typography>
                    )}
                  </Box>
                )}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Controller
                name="bedrooms"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    type="number"
                    label="Bedrooms"
                    error={!!errors.bedrooms}
                    helperText={errors.bedrooms?.message}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Controller
                name="bathrooms"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    type="number"
                    label="Bathrooms"
                    error={!!errors.bathrooms}
                    helperText={errors.bathrooms?.message}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Controller
                name="square_feet"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    type="number"
                    label="Square Feet"
                    error={!!errors.square_feet}
                    helperText={errors.square_feet?.message}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Controller
                name="rent_amount"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    type="number"
                    label="Rent Amount"
                    error={!!errors.rent_amount}
                    helperText={errors.rent_amount?.message}
                    InputProps={{
                      startAdornment: <AttachMoney sx={{ mr: 1 }} />,
                    }}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12}>
              <Controller
                name="status"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    select
                    label="Status"
                    error={!!errors.status}
                    helperText={errors.status?.message}
                  >
                    <MenuItem value="vacant">Vacant</MenuItem>
                    <MenuItem value="occupied">Occupied</MenuItem>
                    <MenuItem value="maintenance">Under Maintenance</MenuItem>
                  </TextField>
                )}
              />
            </Grid>
            <Grid item xs={12}>
              <Controller
                name="description"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    multiline
                    rows={3}
                    label="Description"
                    error={!!errors.description}
                    helperText={errors.description?.message}
                  />
                )}
              />
            </Grid>
            
            {/* Room Counts Section */}
            <Grid item xs={12}>
              <Typography variant="h6" sx={{ mt: 2, mb: 1 }}>Room Configuration</Typography>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Controller
                name="living_room_count"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    type="number"
                    label="Living Rooms"
                    error={!!errors.living_room_count}
                    helperText={errors.living_room_count?.message}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <Controller
                name="mens_guest_room_count"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    type="number"
                    label="Men's Guest Rooms"
                    error={!!errors.mens_guest_room_count}
                    helperText={errors.mens_guest_room_count?.message}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <Controller
                name="womens_guest_room_count"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    type="number"
                    label="Women's Guest Rooms"
                    error={!!errors.womens_guest_room_count}
                    helperText={errors.womens_guest_room_count?.message}
                  />
                )}
              />
            </Grid>
            
            {/* Bed Configuration Section */}
            <Grid item xs={12}>
              <Typography variant="h6" sx={{ mt: 2, mb: 1 }}>Bed Configuration</Typography>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Controller
                name="single_bed_count"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    type="number"
                    label="Single Beds"
                    error={!!errors.single_bed_count}
                    helperText={errors.single_bed_count?.message}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <Controller
                name="double_bed_count"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    type="number"
                    label="Double Beds"
                    error={!!errors.double_bed_count}
                    helperText={errors.double_bed_count?.message}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <Controller
                name="max_persons"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    type="number"
                    label="Max Persons"
                    error={!!errors.max_persons}
                    helperText={errors.max_persons?.message}
                  />
                )}
              />
            </Grid>
            
            {/* Check-in/Check-out Times Section */}
            <Grid item xs={12}>
              <Typography variant="h6" sx={{ mt: 2, mb: 1 }}>Check-in/Check-out Times</Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Controller
                name="checkin_time"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    type="time"
                    label="Check-in Time"
                    error={!!errors.checkin_time}
                    helperText={errors.checkin_time?.message}
                    InputLabelProps={{
                      shrink: true,
                    }}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Controller
                name="checkout_time"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    type="time"
                    label="Check-out Time"
                    error={!!errors.checkout_time}
                    helperText={errors.checkout_time?.message}
                    InputLabelProps={{
                      shrink: true,
                    }}
                  />
                )}
              />
            </Grid>
            
            {/* Tax Rate Section */}
            <Grid item xs={12}>
              <Typography variant="h6" sx={{ mt: 2, mb: 1 }}>Financial Settings</Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Controller
                name="tax_rate"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    value={field.value || ''}
                    fullWidth
                    type="number"
                    label="Tax Rate (%)"
                    error={!!errors.tax_rate}
                    helperText={errors.tax_rate?.message}
                    InputProps={{
                      endAdornment: <Typography variant="caption">%</Typography>,
                    }}
                  />
                )}
              />
            </Grid>
            
            {onUpdateAllRatesChange && (
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={updateAllRates}
                      onChange={(e) => onUpdateAllRatesChange(e.target.checked)}
                    />
                  }
                  label="Update All Rates"
                />
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                  When checked, all daily rates for this unit will be updated to match the new rent amount.
                </Typography>
              </Grid>
            )}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={isLoading}
          >
            {isLoading ? 'Updating...' : 'Update Unit'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default EditUnitDialog; 